import React from 'react';
import {
  ListItem,
  ListItemText,
  Typography,
  Paper,
  Box,
  Badge,
  IconButton,
  Tooltip,
  Avatar,
  Chip,
  useTheme,
  useMediaQuery
} from '@mui/material';
import { 
  DoneAll as ReadIcon,
  Person as PersonIcon,
  Support as SupportIcon,
  CheckCircle as CheckIcon
} from '@mui/icons-material';
import { Message } from '../../api/chatService';
import { blue, grey } from '@mui/material/colors';
import { useThemeContext } from '../../context/ThemeContext';

interface ChatMessageProps {
  message: Message;
  onMarkAsRead: (messageId: number) => void;
}

const ChatMessage: React.FC<ChatMessageProps> = ({ message, onMarkAsRead }) => {
  // Theme for responsive design
  const theme = useTheme();
  const { mode } = useThemeContext();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  // Format the timestamp
  const formattedTime = new Date(message.created_at).toLocaleString(undefined, {
    month: isMobile ? 'numeric' : 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });

  // Format date for message groups
  const formattedDate = new Date(message.created_at).toLocaleDateString(undefined, {
    weekday: isMobile ? 'short' : 'long',
    month: isMobile ? 'short' : 'long',
    day: 'numeric',
    year: 'numeric'
  });

  // Determine if it's an incoming (from Slack) or outgoing message
  const isFromSlack = message.is_from_slack;
  
  // Get first letter of sender name for avatar
  const getInitial = (name: string) => {
    return name && name.length > 0 ? name.charAt(0).toUpperCase() : 'S';
  };
  
  return (
    <ListItem
      alignItems="flex-start"
      sx={{
        px: isMobile ? 0.5 : 1,
        py: isMobile ? 0.25 : 0.5,
        mb: isMobile ? 0.5 : 1
      }}
    >
      <Box
        sx={{
          display: 'flex',
          width: '100%',
          justifyContent: isFromSlack ? 'flex-start' : 'flex-end',
        }}
      >
        {/* User avatar (only show for incoming messages) */}
        {isFromSlack && (
          <Avatar
            sx={{ 
              width: isMobile ? 28 : 36, 
              height: isMobile ? 28 : 36, 
              mr: isMobile ? 0.75 : 1.5,
              mt: 0.5,
              bgcolor: blue[700]
            }}
          >
            {message.sender_name ? 
              getInitial(message.sender_name) : 
              <SupportIcon fontSize="small" />
            }
          </Avatar>
        )}
        
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            maxWidth: isMobile ? '85%' : '75%'
          }}
        >
          {/* Sender name - only for incoming messages */}
          {isFromSlack && message.sender_name && (
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
              <Typography 
                variant={isMobile ? "body2" : "subtitle2"} 
                sx={{ 
                  fontWeight: 600, 
                  color: 'text.primary',
                  fontSize: isMobile ? '0.8rem' : 'inherit'
                }}
              >
                {message.sender_name}
              </Typography>
              {!isMobile && (
                <Chip 
                  label="Support Team" 
                  size="small" 
                  sx={{ 
                    ml: 1, 
                    height: 20, 
                    fontSize: '0.65rem',
                    bgcolor: blue[50],
                    color: blue[700],
                    fontWeight: 500
                  }} 
                />
              )}
            </Box>
          )}
          
          {/* Message bubble */}
          <Paper
            elevation={0}
            sx={{
              p: isMobile ? 1.5 : 2,
              borderRadius: isFromSlack 
                ? isMobile ? '8px 8px 8px 0' : '12px 12px 12px 0' 
                : isMobile ? '8px 8px 0 8px' : '12px 12px 0 12px',
              bgcolor: isFromSlack 
                ? (mode === 'dark' ? '#1a2635' : 'white')
                : (mode === 'dark' ? blue[800] : blue[600]),
              color: isFromSlack 
                ? (mode === 'dark' ? '#ffffff' : 'text.primary')
                : 'white',
              border: isFromSlack 
                ? (mode === 'dark' ? '1px solid rgba(255, 255, 255, 0.1)' : `1px solid ${grey[200]}`)
                : 'none',
              position: 'relative',
              mb: 0.5
            }}
          >
            <Badge
              color="error"
              variant="dot"
              invisible={Boolean(message.is_read) || !isFromSlack}
              sx={{ position: 'absolute', top: 8, right: 8 }}
            />
            
            <Typography 
              variant={isMobile ? "body2" : "body1"}
              sx={{ 
                wordBreak: 'break-word',
                whiteSpace: 'pre-line',
                lineHeight: 1.4,
                fontSize: isMobile ? '0.875rem' : '1rem',
                color: isFromSlack 
                  ? (mode === 'dark' ? '#ffffff' : 'text.primary')
                  : 'white'
              }}
            >
              {message.content}
            </Typography>
          </Paper>
          
          {/* Time and read status */}
          <Box 
            sx={{ 
              display: 'flex', 
              alignItems: 'center',
              justifyContent: isFromSlack ? 'flex-start' : 'flex-end'
            }}
          >
            <Typography 
              variant="caption" 
              color="text.secondary" 
              sx={{ fontSize: '0.7rem' }}
            >
              {formattedTime}
            </Typography>
            
            {isFromSlack && !message.is_read ? (
              <Tooltip title="Mark as read">
                <IconButton 
                  size="small" 
                  onClick={() => onMarkAsRead(message.id)}
                  sx={{ ml: 0.5, p: 0, width: 16, height: 16 }}
                >
                  <ReadIcon fontSize="inherit" color="action" />
                </IconButton>
              </Tooltip>
            ) : !isFromSlack && (
              <Tooltip title="Delivered">
                <CheckIcon 
                  fontSize="inherit" 
                  sx={{ 
                    ml: 0.5,
                    color: 'text.secondary',
                    fontSize: '0.8rem'
                  }} 
                />
              </Tooltip>
            )}
          </Box>
        </Box>
        
        {/* User avatar (only show for outgoing messages) */}
        {!isFromSlack && (
          <Avatar
            sx={{ 
              width: isMobile ? 28 : 36, 
              height: isMobile ? 28 : 36, 
              ml: isMobile ? 0.75 : 1.5,
              mt: 0.5,
              bgcolor: mode === 'dark' ? blue[800] : grey[800]
            }}
          >
            <PersonIcon fontSize="small" />
          </Avatar>
        )}
      </Box>
    </ListItem>
  );
};

export default ChatMessage;